import { useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

/** Per-product image upload/removal. Gated by the super-admin per-tenant
 *  `allowTenantProductImageUpload` flag, which the products service enforces.
 *  The image is uploaded to the products service (→ S3) and stored on
 *  `products.image_url`, then synced to POS where it takes priority over the
 *  generic (common) image.
 *
 *  Uses productsApi (not a raw fetch) so uploads inherit the shared api-client's
 *  403 NO_TENANT refresh-and-retry — a raw fetch 403s outright when the access
 *  token's tenantId is stale/empty. */
export function useUploadProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, file }: { productId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return productsApi.postForm(`/products/${productId}/image`, formData);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (productId: string) => {
      await productsApi.delete(`/products/${productId}/image`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
